const DEFAULT_CURRENCY = "USD";
const DEFAULT_LOCALE = "en-US";

export const CASHING_UP_SESSION_STATUSES = [
  "OPEN",
  "CASH_UP_IN_PROGRESS",
  "COUNT_SUBMITTED",
  "VARIANCE_PENDING",
  "CLOSED",
  "REOPENED",
];

export const CASHING_UP_APPROVAL_STATUSES = [
  "PENDING",
  "APPROVED",
  "REJECTED",
  "NOT_REQUIRED",
];

export const CASHING_UP_MOVEMENT_TYPES = [
  "PAID_IN",
  "PAID_OUT",
  "CASH_DROP",
  "TIP_PAYOUT",
  "FLOAT_ADJUSTMENT",
  "NO_SALE_OPEN",
];

export const CASHING_UP_MOVEMENT_STATUSES = [
  "PENDING",
  "SUBMITTED",
  "VOIDED",
  "REJECTED",
];

export const CASHING_UP_SESSION_STATUS_LABELS = {
  OPEN: "Open",
  CASH_UP_IN_PROGRESS: "Cash-up in progress",
  COUNT_SUBMITTED: "Count submitted",
  VARIANCE_PENDING: "Variance recorded",
  CLOSED: "Closed",
  REOPENED: "Reopened",
};

export const CASHING_UP_APPROVAL_STATUS_LABELS = {
  PENDING: "Pending",
  APPROVED: "Approved",
  REJECTED: "Rejected",
  NOT_REQUIRED: "Not required",
};

export const CASHING_UP_MOVEMENT_TYPE_LABELS = {
  PAID_IN: "Paid in",
  PAID_OUT: "Paid out",
  CASH_DROP: "Cash drop",
  TIP_PAYOUT: "Tip payout",
  FLOAT_ADJUSTMENT: "Float adjustment",
  NO_SALE_OPEN: "No sale open",
};

export const CASHING_UP_MOVEMENT_STATUS_LABELS = {
  PENDING: "Pending",
  SUBMITTED: "Submitted",
  VOIDED: "Voided",
  REJECTED: "Rejected",
};

export const CASHING_UP_MOVEMENT_TYPE_IMPACT = {
  PAID_IN: "positive",
  PAID_OUT: "negative",
  CASH_DROP: "negative",
  TIP_PAYOUT: "negative",
  FLOAT_ADJUSTMENT: "positive",
  NO_SALE_OPEN: "neutral",
};

export const parseJsonString = (value, fallback = null) => {
  if (value === null || value === undefined || value === "") {
    return fallback;
  }

  if (typeof value !== "string") {
    return value;
  }

  try {
    return JSON.parse(value);
  } catch {
    return fallback;
  }
};

export const getTenderSummaryAmount = (
  reconciliationData,
  tenderType,
  providerOrChannel = "POS",
) => {
  const normalizedTenderType = String(tenderType || "").toUpperCase();
  const normalizedProviderOrChannel = String(providerOrChannel || "").toUpperCase();

  const sources = [
    reconciliationData?.tenderSummaries,
    parseJsonString(reconciliationData?.expectedTenderTotalsJson, []),
    parseJsonString(reconciliationData?.session?.expectedTenderTotalsJson, []),
  ];

  for (const source of sources) {
    if (!Array.isArray(source)) continue;

    const match = source.find((tender) => {
      if (!tender) return false;
      const tenderTypeMatch = String(tender.tenderType || "").toUpperCase() === normalizedTenderType;
      const providerMatch =
        !normalizedProviderOrChannel ||
        String(tender.providerOrChannel || "").toUpperCase() === normalizedProviderOrChannel;
      return tenderTypeMatch && providerMatch;
    });

    if (match) {
      return normalizeMoney(
        match.expectedAmount ?? match.amount ?? match.actualAmount ?? match.value ?? 0,
      );
    }
  }

  return 0;
};

export const stringifyJsonValue = (value, fallback = "{}") => {
  if (value === null || value === undefined || value === "") {
    return fallback;
  }

  if (typeof value === "string") {
    return value;
  }

  try {
    return JSON.stringify(value);
  } catch {
    return fallback;
  }
};

export const normalizeMoney = (value) => {
  const amount = Number(value);
  if (!Number.isFinite(amount)) return 0;
  return Math.round((amount + Number.EPSILON) * 100) / 100;
};

export const formatCurrency = (value, { locale = DEFAULT_LOCALE, currency = DEFAULT_CURRENCY } = {}) =>
  new Intl.NumberFormat(locale, {
    style: "currency",
    currency,
  }).format(normalizeMoney(value));

export const formatSignedCurrency = (value, options = {}) => {
  const amount = normalizeMoney(value);
  const formatted = formatCurrency(Math.abs(amount), options);
  if (amount > 0) return `+${formatted}`;
  if (amount < 0) return `-${formatted}`;
  return formatted;
};

export const getMovementSignedAmount = (movementType, amount) => {
  const normalizedAmount = normalizeMoney(amount);
  const impact = CASHING_UP_MOVEMENT_TYPE_IMPACT[movementType] || "neutral";

  if (impact === "positive") {
    return Math.abs(normalizedAmount);
  }

  if (impact === "negative") {
    return -Math.abs(normalizedAmount);
  }

  return normalizedAmount;
};

export const getCashingUpSessionStatusLabel = (status) =>
  CASHING_UP_SESSION_STATUS_LABELS[status] || status || "Unknown";

export const getCashingUpApprovalStatusLabel = (status) =>
  CASHING_UP_APPROVAL_STATUS_LABELS[status] || status || "Unknown";

export const getCashingUpMovementTypeLabel = (movementType) =>
  CASHING_UP_MOVEMENT_TYPE_LABELS[movementType] || movementType || "Unknown";

export const getCashingUpMovementStatusLabel = (status) =>
  CASHING_UP_MOVEMENT_STATUS_LABELS[status] || status || "Unknown";

export const getCashingUpStatusTone = (status) => {
  switch (status) {
    case "APPROVED":
    case "CLOSED":
      return "success";
    case "REJECTED":
      case "VARIANCE_PENDING":
      return "warning";
    case "CASH_UP_IN_PROGRESS":
    case "COUNT_SUBMITTED":
      return "warning";
    case "REOPENED":
      return "info";
    default:
      return "neutral";
  }
};

export const getCashingUpMovementTone = (movementType) => {
  const impact = CASHING_UP_MOVEMENT_TYPE_IMPACT[movementType];
  if (impact === "positive") return "success";
  if (impact === "negative") return "danger";
  return "neutral";
};

export const getCashingUpMovementStatusTone = (status) => {
  switch (status) {
    case "SUBMITTED":
      return "success";
    case "PENDING":
      return "warning";
    case "VOIDED":
    case "REJECTED":
      return "danger";
    default:
      return "neutral";
  }
};

export const isVisibleCashMovementStatus = (status) => {
  const normalizedStatus = String(status || "").toUpperCase();
  return normalizedStatus !== "VOIDED" && normalizedStatus !== "REJECTED";
};
